import {
  Button,
  Paper,
  SegmentedControl,
  Select,
  TextInput,
} from '@mantine/core';
import { IconCheck, IconFileDownload, IconSearch } from '@tabler/icons-react';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { OsLotReviewClause } from '../../lib/os-lot-review-scope';

import classes from './os-lot-reviews.module.css';
import {
  OsLotReviewsQuickFilter,
  useOsLotReviewsFilters,
} from './use-os-lot-reviews-filters';

export interface OsLotReviewsHeaderProps {
  total: number | undefined;
  allOk: number | undefined;
  hasIssue: number | undefined;
  statsLoading?: boolean;
  years: number[];
  species: { value: string; label: string }[];
  clause: OsLotReviewClause;
  campaignYear: number;
  onClauseChange: (clause: OsLotReviewClause) => void;
  onCampaignYearChange: (year: number) => void;
  onExport?: () => void;
  isExportLoading?: boolean;
  selectedCount?: number;
  onAttestSelected?: () => void;
  isAttestSelectedLoading?: boolean;
}

type QueueValue = 'issues' | 'all' | 'ok';

function formatCount(value: number | undefined, statsLoading?: boolean) {
  if (statsLoading && value === undefined) {
    return '—';
  }
  if (value === undefined) {
    return '—';
  }
  return value.toLocaleString('fr-FR');
}

function QueueLabel({
  active,
  count,
  emphasize,
  label,
}: {
  active?: boolean;
  count: string;
  emphasize?: boolean;
  label: string;
}) {
  const countClass = emphasize
    ? active
      ? classes.segCountIssueOn
      : classes.segCountIssue
    : classes.segCount;

  return (
    <span className={classes.segLabel}>
      {label}
      <span className={countClass}>{count}</span>
    </span>
  );
}

export const OsLotReviewsHeader: FC<OsLotReviewsHeaderProps> = ({
  total,
  allOk,
  hasIssue,
  statsLoading,
  years,
  species,
  clause,
  campaignYear,
  onClauseChange,
  onCampaignYearChange,
  onExport,
  isExportLoading,
  selectedCount = 0,
  onAttestSelected,
  isAttestSelectedLoading,
}) => {
  const { t } = useTranslation();
  const {
    searchInput,
    setSearchInput,
    year,
    speciesId,
    queueValue,
    reviewStatus,
    hasData,
    quickFilters,
    toggleQuickFilter,
    handleYearChange,
    handleSpeciesChange,
    handleQueueChange,
    handleReviewStatusChange,
    handleHasDataChange,
  } = useOsLotReviewsFilters();

  const quickFilterDefs: { key: OsLotReviewsQuickFilter; label: string }[] = [
    { key: 'yield_fail', label: t('osLotReviews.filters.pill_yield') },
    { key: 'tonnage_fail', label: t('osLotReviews.filters.pill_tonnage') },
    { key: 'area_fail', label: t('osLotReviews.filters.pill_area') },
  ];

  const anomaliesDisabled = queueValue === 'ok';
  const isNgp = clause === 'new';
  const yearOptions = (
    isNgp && !years.includes(campaignYear) ? [campaignYear, ...years] : years
  )
    .slice()
    .sort((left, right) => right - left);

  return (
    <Paper className={classes.header} p="md" withBorder={false}>
      <div className={classes.topRow}>
        <h1 className={classes.pageTitle}>{t('osLotReviews.page_title')}</h1>
        <SegmentedControl
          aria-label={t('osLotReviews.filters.queue_label')}
          classNames={{
            indicator: classes.queueIndicator,
            innerLabel: classes.queueInner,
            root: classes.queueRoot,
          }}
          color="pf-green"
          data={[
            {
              value: 'issues',
              label: (
                <QueueLabel
                  active={queueValue === 'issues'}
                  count={formatCount(hasIssue, statsLoading)}
                  emphasize={(hasIssue ?? 0) > 0}
                  label={t('osLotReviews.queue.issues')}
                />
              ),
            },
            {
              value: 'all',
              label: (
                <QueueLabel
                  count={formatCount(total, statsLoading)}
                  label={t('osLotReviews.queue.all')}
                />
              ),
            },
            {
              value: 'ok',
              label: (
                <QueueLabel
                  count={formatCount(allOk, statsLoading)}
                  label={t('osLotReviews.queue.ok')}
                />
              ),
            },
          ]}
          onChange={(value) => handleQueueChange(value as QueueValue)}
          radius="sm"
          size="md"
          value={queueValue}
        />
      </div>

      <div className={classes.tools}>
        <div className={classes.filters}>
          <div
            aria-label={t('osLotReviews.filters.mode_label')}
            className={classes.toggles}
            role="radiogroup"
          >
            <Button
              aria-checked={clause === 'gp'}
              className={classes.toggleButton}
              color={clause === 'gp' ? 'pf-green' : 'gray'}
              fw={500}
              onClick={() => onClauseChange('gp')}
              role="radio"
              size="sm"
              tt="none"
              type="button"
              variant={clause === 'gp' ? 'filled' : 'default'}
            >
              {t('osLotReviews.filters.mode_gp')}
            </Button>
            <Button
              aria-checked={isNgp}
              className={classes.toggleButton}
              color={isNgp ? 'pf-green' : 'gray'}
              fw={500}
              onClick={() => onClauseChange('new')}
              role="radio"
              size="sm"
              tt="none"
              type="button"
              variant={isNgp ? 'filled' : 'default'}
            >
              {t('osLotReviews.filters.mode_ngp')}
            </Button>
          </div>
          <TextInput
            aria-label={t('osLotReviews.filters.search_placeholder')}
            className={classes.searchInput}
            leftSection={<IconSearch size={16} stroke={1.5} />}
            onChange={(e) => setSearchInput(e.currentTarget.value)}
            placeholder={t('osLotReviews.filters.search_placeholder')}
            size="sm"
            value={searchInput}
            variant="filled"
          />
        <Select
          aria-label={t('osLotReviews.filters.year')}
          className={classes.filterSelect}
          clearable={!isNgp}
          comboboxProps={{ withinPortal: true }}
          data={yearOptions.map((y) => ({
            value: y.toString(),
            label: y.toString(),
          }))}
          onChange={(value) => {
            if (isNgp) {
              if (value) {
                onCampaignYearChange(Number(value));
              }
              return;
            }
            handleYearChange(value);
          }}
          placeholder={t('osLotReviews.filters.year')}
          size="sm"
          value={isNgp ? String(campaignYear) : year}
          variant="filled"
        />
        <Select
          aria-label={t('osLotReviews.filters.species')}
          className={classes.filterSelect}
          clearable
          comboboxProps={{ withinPortal: true }}
          data={species}
          onChange={handleSpeciesChange}
          placeholder={t('osLotReviews.filters.species')}
          size="sm"
          value={speciesId}
          variant="filled"
        />
        <Select
          aria-label={t('osLotReviews.filters.review_status')}
          className={classes.statusSelect}
          clearable
          comboboxProps={{ withinPortal: true }}
          data={[
            {
              value: 'pending',
              label: t('osLotReviews.review_status.pending'),
            },
            {
              value: 'attested',
              label: t('osLotReviews.review_status.attested'),
            },
            {
              value: 'correction_proposed',
              label: t('osLotReviews.review_status.correction_proposed'),
            },
          ]}
          onChange={handleReviewStatusChange}
          placeholder={t('osLotReviews.filters.review_status')}
          size="sm"
          value={reviewStatus}
          variant="filled"
        />
        </div>

        <div className={classes.actions}>
          <div
            aria-label={t('osLotReviews.filters.anomalies_label')}
            className={classes.toggles}
            role="group"
          >
          {quickFilterDefs.map(({ key, label }) => {
            const active = quickFilters.has(key);
            return (
              <Button
                aria-pressed={active}
                className={classes.toggleButton}
                color={active ? 'pf-green' : 'gray'}
                disabled={anomaliesDisabled}
                fw={500}
                key={key}
                onClick={() => toggleQuickFilter(key)}
                size="sm"
                tt="none"
                variant={active ? 'light' : 'default'}
              >
                {label}
              </Button>
            );
          })}
          <Button
            aria-pressed={hasData}
            className={classes.toggleButton}
            color={hasData ? 'pf-green' : 'gray'}
            fw={500}
            onClick={() => handleHasDataChange(!hasData)}
            size="sm"
            tt="none"
            variant={hasData ? 'light' : 'default'}
          >
            {t('osLotReviews.filters.has_data')}
          </Button>
        </div>
          {onAttestSelected ? (
            <Button
              color="pf-green"
              disabled={selectedCount === 0}
              fw={500}
              leftSection={<IconCheck size={16} />}
              loading={isAttestSelectedLoading}
              onClick={onAttestSelected}
              size="sm"
              tt="none"
            >
              {t('osLotReviews.attest_selection', { count: selectedCount })}
            </Button>
          ) : null}
          {onExport ? (
            <Button
              className={classes.toggleButton}
              fw={500}
              leftSection={<IconFileDownload size={16} />}
              loading={isExportLoading}
              onClick={onExport}
              size="sm"
              tt="none"
              variant="default"
            >
              {t('common.export')}
            </Button>
          ) : null}
        </div>
      </div>
    </Paper>
  );
};
